import type { TFunction } from 'i18next';
import type { AssessmentResultItemDataFragment } from '../../../Trainee/TraineeReports.api';
import type { ChartSeries, PsychologicalResult, RadarDataLeadership } from '../../model';
import formatScoreOutOf from '../formatters';
import { ml360Height } from '.';
import { convertShortLabel } from '@/components/blocks/AssessmentResults/shared/utils';
import type { Ml360AssessmentResultDataFragment } from '@/pages/AssessmentLauncher/ML360/ml360.api';
import { ml360SeriesColors } from '@/pages/Reports/shared/chartColors';

const parseMl360 = (t: TFunction, items: AssessmentResultItemDataFragment[]): PsychologicalResult | null => {
    const selfItem = items.find(
        i => i.result.__typename === 'ML360Result' && i.moduleInstance?.moduleDefinitionIdentifier === 'ml360-self'
    );
    const buddyItem = items.find(
        i => i.result.__typename === 'ML360Result' && i.moduleInstance?.moduleDefinitionIdentifier === 'ml360-buddy'
    );
    const trainerItem = items.find(
        i => i.result.__typename === 'ML360Result' && i.moduleInstance?.moduleDefinitionIdentifier === 'ml360-trainer'
    );

    const moduleInstance = selfItem?.moduleInstance || buddyItem?.moduleInstance || trainerItem?.moduleInstance;
    if (!moduleInstance?.moduleDefinitionIdentifier) {
        return null;
    }

    const selfResult = selfItem?.result as Ml360AssessmentResultDataFragment | undefined;
    const buddyResult = buddyItem?.result as Ml360AssessmentResultDataFragment | undefined;
    const trainerResult = trainerItem?.result as Ml360AssessmentResultDataFragment | undefined;

    if (
        !selfResult?.categoryAverages?.length &&
        !buddyResult?.categoryAverages?.length &&
        !trainerResult?.categoryAverages?.length
    ) {
        return null;
    }

    const categories = Array.from(
        new Set<string>([
            ...(selfResult?.categoryAverages?.map(c => c.category) || []),
            ...(buddyResult?.categoryAverages?.map(c => c.category) || []),
            ...(trainerResult?.categoryAverages?.map(c => c.category) || []),
        ])
    );
    if (categories.length === 0) {
        return null;
    }

    const radarData: RadarDataLeadership[] = categories.map(category => {
        const selfScore = selfResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        const buddyScore = buddyResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        const trainerScore = trainerResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        return {
            axis: convertShortLabel(category),
            self: Number(selfScore.toFixed(1)),
            buddy: Number(buddyScore.toFixed(1)),
            trainer: Number(trainerScore.toFixed(1)),
        };
    });

    const allCategoryAverages = [
        ...(selfResult?.categoryAverages ?? []),
        ...(buddyResult?.categoryAverages ?? []),
        ...(trainerResult?.categoryAverages ?? []),
    ];
    const maxDomain = Math.max(1, ...(allCategoryAverages.length ? allCategoryAverages.map(c => c.maxScore) : [1]));

    const scoreCards =
        selfResult?.categoryAverages?.map(c => ({
            label: convertShortLabel(c.category),
            value: formatScoreOutOf(t, c.averageScore, c.maxScore, 1),
        })) || [];

    const series: ChartSeries[] = [
        {
            key: 'self',
            name: t('traineeReports:labels.self'),
            stroke: ml360SeriesColors.self.stroke,
            fill: ml360SeriesColors.self.fill,
            fillOpacity: ml360SeriesColors.self.fillOpacity,
        },
        {
            key: 'buddy',
            name: t('traineeReports:labels.buddy'),
            stroke: ml360SeriesColors.buddy.stroke,
            fill: ml360SeriesColors.buddy.fill,
            fillOpacity: ml360SeriesColors.buddy.fillOpacity,
        },
        {
            key: 'trainer',
            name: t('traineeReports:labels.trainer'),
            stroke: ml360SeriesColors.trainer.stroke,
            fill: ml360SeriesColors.trainer.fill,
            fillOpacity: ml360SeriesColors.trainer.fillOpacity,
        },
    ];

    return {
        moduleDefinitionIdentifier: moduleInstance.moduleDefinitionIdentifier ?? '',
        name: moduleInstance.name ?? t('traineeReports:ml360.title'),
        description: moduleInstance.description ?? t('traineeReports:ml360.description'),
        radarGroups: [
            {
                key: 'ml360',
                scoreCards,
                charts: [
                    {
                        data: radarData,
                        series,
                        maxDomain,
                        height: ml360Height,
                        variant: 'bare',
                        footer: t('traineeReports:labels.individual'),
                    },
                ],
            },
        ],
    };
};

export default parseMl360;
